import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { useAuth } from '../contexts/AuthContext';

const AV_COLORS = ['#2383e2', '#e0561d', '#0f9d58', '#a142f4', '#d93025', '#f4b400', '#1abc9c', '#9b59b6'];
function colorFor(id) {
  let h = 0;
  const s = String(id || '');
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
}

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Math.max(0, Date.now() - new Date(iso).getTime());
  const m = Math.floor(diff / 60000);
  if (m < 1) return '방금';
  if (m < 60) return `${m}분 전`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}시간 전`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}일 전`;
  return new Date(iso).toLocaleDateString('ko-KR', { month: 'short', day: 'numeric' });
}

const TYPE_LABEL = {
  mention: '님이 회원님을 멘션했어요',
  comment: '님이 댓글을 남겼어요',
  reply: '님이 답글을 남겼어요',
  share: '님이 페이지를 공유했어요',
};
function typeText(type) {
  return TYPE_LABEL[type] || '님의 새 알림';
}

export default function InboxPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [actorMap, setActorMap] = useState({}); // user_id -> member
  const [loading, setLoading] = useState(true);
  const [errMsg, setErrMsg] = useState('');

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data, error } = await supabase
      .schema('workspace').from('notifications')
      .select('id, org_id, recipient_id, actor_id, type, page_id, comment_id, preview, read, created_at')
      .eq('recipient_id', user.id)
      .order('created_at', { ascending: false });
    if (error) {
      setErrMsg('알림을 불러오지 못했어요.');
      setItems([]);
      setLoading(false);
      return;
    }
    setErrMsg('');
    const list = data || [];
    setItems(list);

    // actor 이름 조회
    const actorIds = Array.from(new Set(list.map((n) => n.actor_id).filter(Boolean)));
    if (actorIds.length > 0) {
      const { data: members } = await supabase
        .schema('workspace').from('members')
        .select('user_id, display_name, avatar_url')
        .in('user_id', actorIds);
      const map = {};
      (members || []).forEach((m) => { map[m.user_id] = m; });
      setActorMap(map);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);

  const unreadCount = items.filter((n) => !n.read).length;

  async function openItem(n) {
    if (!n.read) {
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
      await supabase.schema('workspace').from('notifications')
        .update({ read: true }).eq('id', n.id);
    }
    if (n.page_id) navigate(`/page/${n.page_id}`);
  }

  async function markAllRead() {
    const unread = items.filter((n) => !n.read);
    if (unread.length === 0) return;
    setItems((prev) => prev.map((x) => ({ ...x, read: true })));
    await supabase.schema('workspace').from('notifications')
      .update({ read: true })
      .eq('recipient_id', user.id)
      .eq('read', false);
  }

  if (loading) {
    return (
      <div className="empty"><div className="spinner" /></div>
    );
  }

  if (errMsg) {
    return (
      <div className="empty">
        <div className="big">⚠️</div>
        <div style={{ fontSize: 16, color: 'var(--text)' }}>{errMsg}</div>
        <button type="button" className="ib-retry" onClick={load}>다시 시도</button>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="empty">
        <div className="big">📥</div>
        <div style={{ fontSize: 16, color: 'var(--text)' }}>수신함이 비어 있어요</div>
        <div className="muted">@멘션·댓글·공유 알림이 여기에 모여요.</div>
      </div>
    );
  }

  return (
    <div className="ib-root">
      <header className="ib-head">
        <div className="ib-title">
          수신함
          {unreadCount > 0 ? <span className="ib-count">{unreadCount}</span> : null}
        </div>
        {unreadCount > 0 ? (
          <button type="button" className="ib-mark" onClick={markAllRead}>모두 읽음</button>
        ) : null}
      </header>

      <ul className="ib-list">
        {items.map((n) => {
          const actor = actorMap[n.actor_id];
          const name = actor?.display_name || '알 수 없는 사용자';
          return (
            <li key={n.id}>
              <button
                type="button"
                className={`ib-item${n.read ? '' : ' is-unread'}`}
                onClick={() => openItem(n)}
              >
                {!n.read ? <span className="ib-dot" aria-hidden="true" /> : <span className="ib-dot-sp" />}
                {actor?.avatar_url ? (
                  <img className="ib-avatar" src={actor.avatar_url} alt={name} />
                ) : (
                  <span className="ib-avatar" style={{ background: colorFor(n.actor_id) }}>
                    {name.slice(0, 1)}
                  </span>
                )}
                <span className="ib-body">
                  <span className="ib-line">
                    <span className="ib-name">{name}</span>
                    <span className="ib-action">{typeText(n.type)}</span>
                  </span>
                  {n.preview ? (
                    <span className="ib-preview">
                      {n.type === 'share' ? `📄 ${n.preview}` : n.preview}
                    </span>
                  ) : null}
                  <span className="ib-time">{timeAgo(n.created_at)}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
